import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getSurahInfo } from '../../lib/surahs'
import type { MemorizationData } from '../../types'

interface DueForReviewStripProps {
  verses: MemorizationData[]
}

/**
 * Horizontally-scrolling strip of verses whose nextReviewDate has passed.
 * Each card has a "Practice now" CTA that pre-selects memorization-source
 * in the game setup. Per-verse pre-fill isn't supported by the existing
 * GameSetup yet; the user picks verse count + game type from there.
 * Improving that is a follow-up.
 *
 * The parent (GamesPage) hides this section entirely when verses.length
 * === 0 — there is no empty state inside this component.
 */
export function DueForReviewStrip({ verses }: DueForReviewStripProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  if (verses.length === 0) return null

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.75, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('games.dueForReview')}
        </Typography>
        <Chip
          label={t('games.dueCount', { count: verses.length })}
          size="small"
          sx={{
            color: 'primary.dark',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(45,212,191,0.14)' : '#F0FDFA',
            border: 1,
            borderColor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(94,234,212,0.38)' : '#99F6E4',
          }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('games.dueHint')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 1,
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { height: 6 },
        }}
      >
        {verses.map((v) => {
          const surah = getSurahInfo(v.surahNumber)
          return (
            <Card
              key={`${v.surahNumber}:${v.ayahNumber}`}
              variant="outlined"
              sx={{
                minWidth: { xs: 200, sm: 240 },
                flexShrink: 0,
                scrollSnapAlign: 'start',
              }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {surah?.englishName ?? `Surah ${v.surahNumber}`} · Ayah {v.ayahNumber}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    minHeight: 40,
                  }}
                >
                  {surah?.name ?? `Surah ${v.surahNumber}`}
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  // v1: pre-selects source=memorization. Per-verse pre-fill
                  // would need GameSetup support (deferred).
                  onClick={() =>
                    navigate('/games/multiple-choice', { state: { source: 'memorization' } })
                  }
                >
                  {t('games.practiceNow')}
                </Button>
              </CardContent>
            </Card>
          )
        })}
      </Box>
    </Box>
  )
}
